import React, { ChangeEvent, FocusEvent, forwardRef, ReactNode } from 'react';
import { cx } from '@linaria/core';
import { styled } from '@linaria/react';
import { CaptionWrapper, EndIconsWrapper, getBaseInputIconPaddingStyles, getClassName, InputWrapper, TextTransform, ValidationBadge } from '../helpers/InputBase';
import { Text } from '../Text';
interface Props {
  /** Field autocomplete id */
  autoComplete?: string;
  /** Message to display below input. Changes color along with validation highlights. */
  caption?: ReactNode;
  /** Identify the element for selection in integration tests, FullStory, etc. */
  dataId?: string;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Whether the input has an error */
  hasError?: boolean;
  /** ID of this component, passed down by the controlling parent component */
  id: string;
  /** Whether the input content is private and should be masked in FullStory */
  isPrivate?: boolean;
  /** Whether the input should be resizable */
  isResizable?: boolean;
  /** Whether the user has interacted with this input */
  isTouched?: boolean;
  /** Name of this component, passed down by the controlling parent component */
  name: string;
  /** An onBlur callback passed down by the controlling parent component */
  onBlur?: (event: FocusEvent<HTMLTextAreaElement>) => void;
  /** An onChange callback passed down by the controlling parent component */
  onChange?: (value: string) => void;
  /** Formik function for setting a specific field's value. Used to update the value without sending an HTML event. */
  setFieldValue?: (field: string, value: unknown, shouldValidate?: boolean) => void;
  /** Content to be used as the input placeholder */
  placeholder: string;
  /** The height of the input */
  inputHeight?: number;
  /** CSS text-transform style to apply to the input field */
  textTransformType?: TextTransform;
  /** The value passed down by the controlling parent component */
  value?: string;
  /** Pass through classname to allow styles overrides */
  className?: string;
  /** Style prop is used by Linaria to pass CSS variables in cases when we need to style custom components in this way: `const StyledInputTextarea = styled(InputTextarea)` */
  style?: React.CSSProperties;
}
const DEFAULT_INPUT_HEIGHT = 100;
const _exp8 = /*#__PURE__*/() => ({
  withEndIcon,
  showInvalid,
  showValid
}) => getBaseInputIconPaddingStyles({
  withEndIcon,
  showInvalid,
  showValid
}).paddingLeft;
const _exp9 = /*#__PURE__*/() => ({
  withEndIcon,
  showInvalid,
  showValid
}) => getBaseInputIconPaddingStyles({
  withEndIcon,
  showInvalid,
  showValid
}).paddingRight;
const _exp12 = /*#__PURE__*/() => ({
  textTransformType
}) => textTransformType ? textTransformType : TextTransform.none;
const _exp13 = /*#__PURE__*/() => ({
  inputHeight
}) => inputHeight;
export const InputTextareaElement = /*#__PURE__*/styled('textarea')({
  name: "InputTextareaElement",
  class: "i1gn1vr",
  propsAsIs: false,
  vars: {
    "i1gn1vr-0": [_exp8()],
    "i1gn1vr-1": [_exp9()],
    "i1gn1vr-2": [_exp12()],
    "i1gn1vr-3": [_exp13(), "px"]
  }
});
const _exp14 = /*#__PURE__*/() => EndIconsWrapper;
export const ValidationBadgeWrapper = /*#__PURE__*/styled(_exp14())({
  name: "ValidationBadgeWrapper",
  class: "v1selhms",
  propsAsIs: true
});
export const InputTextarea = forwardRef<HTMLTextAreaElement, Props>(({
  autoComplete,
  dataId,
  disabled,
  caption,
  hasError = false,
  id,
  inputHeight = DEFAULT_INPUT_HEIGHT,
  isPrivate = false,
  isResizable = true,
  isTouched = false,
  name,
  onBlur,
  onChange,
  placeholder,
  setFieldValue,
  textTransformType = TextTransform.none,
  value,
  className,
  style
}, ref) => {
  const showInvalid = isTouched && hasError;
  const showValid = isTouched && !hasError;
  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = event.currentTarget.value;
    if (setFieldValue) {
      setFieldValue(name, newValue);
    } else if (onChange) {
      onChange(newValue);
    }
  };
  return <InputWrapper className={getClassName(isPrivate)}>
        <InputTextareaElement ref={ref} autoComplete={autoComplete} className={cx(showInvalid && 'isInvalid', isResizable && 'isResizable', className)} data-field-error={hasError} data-id={dataId} disabled={disabled} id={id} inputHeight={inputHeight} showInvalid={showInvalid} showValid={showValid} name={name} placeholder={placeholder} textTransformType={textTransformType} value={value} withEndIcon={false} onBlur={onBlur} onChange={handleChange} style={style} />

        <ValidationBadgeWrapper>
          <ValidationBadge showInvalid={showInvalid} showValid={showValid} />
        </ValidationBadgeWrapper>

        {caption && <CaptionWrapper showInvalid={showInvalid}>
            <Text variant="labelXS">{caption}</Text>
          </CaptionWrapper>}
      </InputWrapper>;
});
InputTextarea.displayName = 'InputTextarea';

require("./InputTextarea.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./InputTextarea.tsx");